import { useEffect, useState } from "react";
import type { LiveListing } from "../lib/api";
import { fmtCents, fmtTimeLeft } from "../lib/format";
import DealScoreChip from "./DealScoreChip";
import StatusPill from "./StatusPill";
import CompsDialog from "./CompsDialog";

type Field = "resale_floor" | "sold_floor" | "sold_median" | "bin_floor";

type Props = {
  listing: LiveListing;
  alertThreshold: number;
  pulsed?: boolean;
  // Rendered as a small column when the dashboard is in "All searches"
  // mode so users can see which saved search a row came from. Omitting
  // it suppresses the column entirely (single-search view).
  searchName?: string;
};

export default function ListingRow({
  listing,
  alertThreshold,
  pulsed,
  searchName,
}: Props) {
  const [now, setNow] = useState(() => new Date().toISOString());
  const [openField, setOpenField] = useState<Field | null>(null);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date().toISOString()), 1000);
    return () => clearInterval(t);
  }, []);

  const canDrillDown = listing.status === "priced" || listing.status === "no_comps";
  const cellClass = (val: number | null): string =>
    `px-3 py-2 text-right font-mono text-xs ${
      canDrillDown && val !== null
        ? "cursor-pointer underline decoration-dotted decoration-zinc-700 hover:bg-zinc-800/60 hover:text-zinc-100"
        : ""
    }`;
  const onCellClick = (field: Field, val: number | null) => {
    if (canDrillDown && val !== null) setOpenField(field);
  };

  return (
    <>
      <tr className={`border-b border-zinc-900 hover:bg-zinc-900/40 ${pulsed ? "animate-pulse bg-green-900/30" : ""}`}>
        <td className="px-3 py-2 text-xs font-mono whitespace-nowrap text-zinc-300">{fmtTimeLeft(listing.end_time, now)}</td>
        {searchName !== undefined && (
          <td className="px-3 py-2 text-xs font-mono whitespace-nowrap text-zinc-500 max-w-[10rem] truncate">
            {searchName}
          </td>
        )}
        <td className="px-3 py-2 max-w-md">
          <a href={listing.url} target="_blank" rel="noreferrer" className="hover:text-zinc-100 text-zinc-300 truncate block">
            {listing.title}
          </a>
        </td>
        <td className="px-3 py-2 text-right font-mono text-xs">{fmtCents(listing.total_cents)}</td>
        <td
          className={cellClass(listing.resale_floor_cents)}
          onClick={() => onCellClick("resale_floor", listing.resale_floor_cents)}
        >
          {fmtCents(listing.resale_floor_cents)}
        </td>
        <td
          className={`${cellClass(listing.sold_floor_cents)} text-zinc-500`}
          onClick={() => onCellClick("sold_floor", listing.sold_floor_cents)}
        >
          {fmtCents(listing.sold_floor_cents)}
        </td>
        <td
          className={`${cellClass(listing.sold_median_cents)} text-zinc-500`}
          onClick={() => onCellClick("sold_median", listing.sold_median_cents)}
        >
          {fmtCents(listing.sold_median_cents)}
        </td>
        <td
          className={`${cellClass(listing.bin_floor_cents)} text-zinc-500`}
          onClick={() => onCellClick("bin_floor", listing.bin_floor_cents)}
        >
          {fmtCents(listing.bin_floor_cents)}
        </td>
        <td className="px-3 py-2 text-center"><DealScoreChip score={listing.deal_score} threshold={alertThreshold} /></td>
        <td className="px-3 py-2 text-center text-xs font-mono text-zinc-500">{listing.sold_sample}/{listing.bin_sample}</td>
        <td className="px-3 py-2 text-center"><StatusPill status={listing.status} /></td>
      </tr>
      {openField && (
        <CompsDialog
          listingId={listing.id}
          field={openField}
          onClose={() => setOpenField(null)}
        />
      )}
    </>
  );
}
